import { Box, Paper, Typography } from "@mui/material";
import { renderSubTitle } from "@/components/renderComponents";

function InfoCard({ title, body }: { title: string; body: string }) {
  return (
    <Paper elevation={0} className="market-reports-info-card">
      <Typography className="text-general-page-subheader" sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      <Typography className="text-general-page-text">{body}</Typography>
    </Paper>
  );
}

function InfoBullets({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map((item) => (
        <Box component="li" key={item} sx={{ mb: 0.5 }}>
          <Typography className="text-general-page-text">{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function InfoTab() {
  return (
    <Box className="market-reports-info-tab">
      <Typography className="text-general-page-text" sx={{ mb: 2 }}>
        Upload broker and research PDFs. The system reads each report and pulls
        out market data, summaries, and quotes from the original pages. You can
        then browse those reports, read combined market summaries, or ask
        questions, with links back to the source material.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          mb: 2.5,
        }}
      >
        <InfoCard
          title="Reports"
          body="Your library of uploaded PDFs. Upload one file or many at once, track processing status, and open any report for full detail."
        />
        <InfoCard
          title="Market"
          body="A ready-made summary for a location and property type (e.g. Poland · Residential), drawn from all relevant reports."
        />
        <InfoCard
          title="Chat"
          body="Ask your own questions across all processed reports. Useful for comparisons, specific metrics, or follow-up detail."
        />
      </Box>

      <Paper elevation={0} className="market-reports-info-tip" sx={{ mb: 3 }}>
        <Typography className="text-general-page-text">
          Not sure where to start? Use{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            Market
          </Box>{" "}
          for a sector overview,{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            Chat
          </Box>{" "}
          for a specific question, and{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            Reports
          </Box>{" "}
          to upload or inspect the underlying PDFs.
        </Typography>
      </Paper>

      <Box sx={{ mb: 2.5 }}>
        {renderSubTitle("Reports")}
        <Box sx={{ mt: 1.5 }}>
          <InfoBullets
            items={[
              "Upload single or batch PDFs. Duplicates are detected automatically.",
              "Click a report to see coverage by location and sector, extracted metrics, and source quotes.",
              "Download the original file or delete a report from the detail page.",
            ]}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        {renderSubTitle("Market")}
        <Box sx={{ mt: 1.5 }}>
          <InfoBullets
            items={[
              "Pick a location and sector from the dropdown.",
              "Read the combined summary and follow source links to individual reports.",
              "Refresh if the summary is missing, out of date, or based on only a few reports.",
              "If a market is missing, check geography and sector names on a report’s coverage tab. Labels must match exactly.",
            ]}
          />
        </Box>
      </Box>

      <Box>
        {renderSubTitle("Chat")}
        <Box sx={{ mt: 1.5 }}>
          <InfoBullets
            items={[
              "Type a question in plain English (e.g. “What is logistics occupancy in France?”).",
              "Answers cite the reports they came from, with page references where available.",
              "Tables and charts appear when the system finds comparable figures across reports.",
            ]}
          />
        </Box>
      </Box>
    </Box>
  );
}
